// Copyright (c) 2025 WiseTime. All rights reserved.

import { Action } from "redux"
import { EMPTY, Observable, of } from "rxjs"
import { catchError, filter, mergeMap } from "rxjs/operators"
import * as Slice from "../slice"
import * as Actions from "../actions"
import { grpc } from "../../../app/rpc/executor"
import { userTimelineMessageClient } from "../../../app/rpc/client"
import { convertPbDateToTimelineDate, getTimelineDatesInRange } from "../../timeline/dateTime"
import {
  ListUserTimelineMessagesRequest,
  ListUserTimelineMessagesResponse,
} from "../../../generated/wisetime/user/timelinemessage/v4/user_timeline_message_pb"

type ListUserTimelineMessagesRpc = (request: ListUserTimelineMessagesRequest) =>
  Observable<ListUserTimelineMessagesResponse>

export const listUserTimelineMessages = (listUserTimelineMessagesRpc: ListUserTimelineMessagesRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.listUserTimelineMessages.match),
      mergeMap(({ payload }) =>
        of(payload)
          .pipe(
            mergeMap(listUserTimelineMessagesRpc),
            mergeMap(response => {
              const { startDate, endDate } = payload.toObject()
              const { timelineMessagesList } = response.toObject()
              if (startDate === undefined || endDate === undefined) {
                return EMPTY
              }
              const result: Slice.SetTimelineMessagesPayload[] = getTimelineDatesInRange(
                convertPbDateToTimelineDate(startDate),
                convertPbDateToTimelineDate(endDate),
              ).map(date => ({
                cacheKey: Slice.timelineMessagesCacheKey(date, payload.getSiteId()),
                timelineMessagesList: timelineMessagesList.filter(m => (
                  m.date && convertPbDateToTimelineDate(m.date) === date
                )),
              }))
              return of(Slice.setTimelineMessages(result))
            }),
            catchError(_ => of(Actions.listUserTimelineMessagesFailed()))
          )
      )
    )

const listUserTimelineMessagesRpc = (
  request: ListUserTimelineMessagesRequest,
): Observable<ListUserTimelineMessagesResponse> =>
  grpc(() => userTimelineMessageClient.listUserTimelineMessages(request, null))

export default listUserTimelineMessages(listUserTimelineMessagesRpc)
